import { ChangeDetectionStrategy, Component } from '@angular/core';
import { DateTime } from 'luxon';
import { ProgressChartSeries, ProgressChartDataPoint, ProgressChartTrend } from 'components/cs-progress-chart/cs-progress-chart';
import { buildRunningTrendRows, RUNNING_TREND_WEIGHT_OPTIONS } from '@cf-shared/running-trend';
import { TrendChartDataset } from 'components/cs-trend-chart/cs-trend-chart';
import { TrendBandChartPoint, TrendBandChartRow } from 'components/cs-trend-band-chart/cs-trend-band-chart';
import { CalorieIntakeBaseline } from '@cf-shared/data-model/log-weekly-checkup';
import { PlanUpdateUserMessage } from '@cf-shared/plan-update-interface';
import { BodyFatSeries } from '@cf-shared/plan-update-internals';
import { BodyCompOverviewModel, OverviewReading, OverviewTrendKind, buildBodyCompOverviewModel } from 'services/body-comp-overview-model';
import { ProgressStatusRule } from 'services/progress-status';
import { untranslatedString } from 'models/translated-string';
import { SAMPLE_TRAINING_OVERVIEW } from './training-overview-sample';
import { buildMonthlyClientBars, CoachHistoryChartRow, MonthlyClientBar } from '@cf-shared/data-model/coach-history-chart';
import { CoachHistoryRoute } from '@cf-shared/data-model/coach-history';

// The Charts card's body on the Test UI Components page (#6020): the progress chart and the trend
// chart, each with a few sample weigh-ins ending today. Since #5456 also the trend band chart and
// the Body Composition overview built from hand-written readings, and the Training Overview drawn
// from a hand-written model. Since #6627 also the running trend: on the progress chart, as the
// check-up's straight tunnel, on the band chart, and its legend. Since #6768 also the coach
// billing page's monthly clients chart, for a Paddle coach and for a store coach with a limit.
@Component({
  selector: 'cs-test-charts-card',
  host: { class: 'showcase-card-body' },
  template: `
    <div class="showcase-grid">
      <div class="showcase-row" data-showcase-id="cs-progress-chart">
        <cs-label>cs-progress-chart</cs-label>
        <cs-progress-chart [series]="chartSeries" [data]="chartData" timePeriod="month"></cs-progress-chart>
      </div>

      <div class="showcase-row" data-showcase-id="cs-progress-chart-running-trend">
        <cs-label>cs-progress-chart with the running trend</cs-label>
        <cs-progress-chart [series]="chartSeries" [data]="longChartData" [trends]="longChartTrends" timePeriod="quarter"></cs-progress-chart>
      </div>

      <div class="showcase-row" data-showcase-id="cs-coach-clients-chart">
        <cs-label>cs-coach-clients-chart</cs-label>
        <cs-coach-clients-chart [bars]="coachClientBars"></cs-coach-clients-chart>
      </div>

      <div class="showcase-row" data-showcase-id="cs-coach-clients-chart-limit">
        <cs-label>cs-coach-clients-chart with a store plan's limit</cs-label>
        <cs-coach-clients-chart [bars]="coachClientBarsWithLimit"></cs-coach-clients-chart>
      </div>

      <div class="showcase-row" data-showcase-id="cs-trend-chart">
        <cs-label>cs-trend-chart</cs-label>
        <cs-trend-chart [title]="'Weight' | untranslated" [unit]="'kg' | untranslated" [datasets]="trendDatasets"
          [days]="trendDays"></cs-trend-chart>
      </div>

      <div class="showcase-row" data-showcase-id="cs-trend-chart-tunnel">
        <cs-label>cs-trend-chart with the straight tunnel</cs-label>
        <cs-trend-chart [title]="'Weight' | untranslated" [unit]="'kg' | untranslated" [datasets]="tunnelDatasets"
          [days]="tunnelDays" [showTunnel]="true"></cs-trend-chart>
      </div>

      <div class="showcase-row" data-showcase-id="cs-trend-band-chart">
        <cs-label>cs-trend-band-chart</cs-label>
        <cs-trend-band-chart [points]="bandPoints" [band]="bandRows" [color]="bandColor" unitName="kg"></cs-trend-band-chart>
      </div>

      <div class="showcase-row" data-showcase-id="cs-trend-band-chart-directions">
        <cs-label>cs-trend-band-chart with directions</cs-label>
        <cs-trend-band-chart [points]="directionPoints" [band]="directionRows" [color]="bandColor" unitName="kg"></cs-trend-band-chart>
      </div>

      <div class="showcase-row" data-showcase-id="cs-trend-legend">
        <cs-label>cs-trend-legend</cs-label>
        <cs-trend-legend></cs-trend-legend>
      </div>

      <div class="showcase-row" data-showcase-id="cs-body-comp-overview">
        <cs-label>cs-body-comp-overview</cs-label>
        <cs-body-comp-overview [model]="overviewModel"></cs-body-comp-overview>
      </div>

      <div class="showcase-row" data-showcase-id="cs-training-overview">
        <cs-label>cs-training-overview</cs-label>
        <!-- the first card as if paged back once: both paging buttons show -->
        <cs-training-overview [model]="trainingOverviewModel" [canShowEarlier]="[true]" [canShowLater]="[true]"></cs-training-overview>
      </div>
    </div>
  `,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsTestChartsCardComponent {
  readonly trainingOverviewModel = SAMPLE_TRAINING_OVERVIEW;

  // the design doc's worked example, ending this month: 1, 2, 3, 3, 5, then 4 awake + 1 asleep
  readonly coachClientBars: MonthlyClientBar[] = buildMonthlyClientBars(sampleCoachRows(CoachHistoryRoute.Paddle, [
    [6, 1, 0], [4, 2, 0], [3, 3, 0], [1, 4, 0], [1, 5, 0], [0, 4, 1], [0, 3, 1],
  ]), 6, DateTime.utc(), DateTime.local().zoneName);
  readonly coachClientBarsWithLimit: MonthlyClientBar[] = buildMonthlyClientBars(sampleCoachRows(CoachHistoryRoute.Apple, [
    [5, 1, 0], [3, 2, 0], [2, 3, 0],
  ], 5), 6, DateTime.utc(), DateTime.local().zoneName);

  readonly chartSeries: ProgressChartSeries[] = [{ id: 'weight', label: 'Weight', colorVar: '--chart-purple', measurementTypeId: 'bodyweight_kg' }];
  readonly chartData: ProgressChartDataPoint[] = [
    { date: DateTime.local().minus({ days: 14 }).toISODate(), weight: 80.4 },
    { date: DateTime.local().minus({ days: 7 }).toISODate(), weight: 79.8 },
    { date: DateTime.local().toISODate(), weight: 79.1 },
  ];
  // the running trend (#6627): 120 days of weigh-ins ending today, a cut of 0.05 kg a day that
  // levels off and turns into a slow gain for the last month, with a small repeating wobble
  private readonly longDays = Array.from({ length: 120 }, (_, i) => DateTime.local().minus({ days: 119 - i }).toISODate());
  private readonly longValues = this.longDays.map((_, i) => {
    const shape = i < 70 ? 84 - i * 0.05 : i < 90 ? 80.5 : 80.5 + (i - 90) * 0.03;
    return Math.round((shape + [0, 0.3, -0.2, 0.1, -0.3, 0.2, -0.1][i % 7]) * 10) / 10;
  });
  readonly longChartData: ProgressChartDataPoint[] = this.longDays.slice(-90).map((date, i) => ({ date, weight: this.longValues[30 + i] }));
  private readonly longRows = buildRunningTrendRows(
    this.longDays.map((dateIso, i) => ({ dateIso, value: this.longValues[i] })),
    { firstDayIso: this.longDays[30], lastDayIso: this.longDays[119], todayIso: this.longDays[119] },
    RUNNING_TREND_WEIGHT_OPTIONS,
  );
  readonly longChartTrends: ProgressChartTrend[] = [{ seriesId: 'weight', rows: this.longRows, showTunnel: true }];
  // the band chart with directions: the last 8 weeks of the same weigh-ins and trend
  readonly directionPoints: TrendBandChartPoint[] = this.longDays.slice(-56).map((dateIso, i) => ({ dateIso, value: this.longValues[64 + i] }));
  readonly directionRows: TrendBandChartRow[] = this.longRows.slice(-56).map(row => ({
    dateIso: row.dateIso, low: row.lower, mid: row.value, high: row.upper, direction: row.direction, provisional: row.isProvisional,
  }));
  // the check-up's chart: 14 daily weigh-ins, of which it draws the last 7, with the straight tunnel
  readonly tunnelDays = Array.from({ length: 14 }, (_, i) => DateTime.local().minus({ days: 13 - i }).toISODate());
  readonly tunnelDatasets: TrendChartDataset[] = [{
    name: 'Weight',
    values: [81.2, 81.0, 81.3, 80.9, 80.8, 80.9, 80.5, 80.7, 80.4, 80.3, 80.4, 80.0, 80.2, 79.9],
    color: getComputedStyle(document.documentElement).getPropertyValue('--chart-purple').trim(),
  }];

  // the colour comes from the theme, the way weekly-checkup-page reads it
  readonly trendDatasets: TrendChartDataset[] = [{
    name: 'Weight',
    values: [80.4, 80.1, 80.2, 79.8, 79.6, 79.5, 79.1],
    color: getComputedStyle(document.documentElement).getPropertyValue('--chart-purple').trim(),
  }];
  // cs-trend-chart's x axis is a time axis, so the days are ISO dates, the last 7 ending today
  readonly trendDays = Array.from({ length: 7 }, (_, i) => DateTime.local().minus({ days: 6 - i }).toISODate());

  // cs-trend-band-chart: 14 daily weigh-ins falling 0.06 kg a day, a straight trend and a tunnel
  // that widens towards both ends
  private readonly bandDays = Array.from({ length: 14 }, (_, i) => DateTime.local().minus({ days: 13 - i }).toISODate());
  private readonly bandJitter = [0, 0.3, -0.2, 0.1, -0.3, 0.2, 0, -0.1, 0.3, -0.2, 0.1, 0, -0.3, 0.1];
  readonly bandColor = getComputedStyle(document.documentElement).getPropertyValue('--chart-purple').trim();
  readonly bandPoints: TrendBandChartPoint[] = this.bandDays.map((dateIso, i) => ({ dateIso, value: 83 - i * 0.06 + this.bandJitter[i] }));
  readonly bandRows: TrendBandChartRow[] = [
    { dateIso: this.bandDays[0], low: 82.8, mid: 83, high: 83.2 },
    { dateIso: this.bandDays[13], low: 82.0, mid: 82.2, high: 82.4 },
  ];

  // cs-body-comp-overview: BORI's two weeks from the #5456 walkthrough. Daily weigh-ins, skinfolds
  // every 5 days, one waist reading a week, and a check-up week with 2 days left unticked.
  private readonly overviewReadings: OverviewReading[] = this.bandDays.map((dateIso, i) => ({
    dateIso,
    weight_kg: 83 - i * 0.06 + this.bandJitter[i],
    ...(i % 5 === 0 ? { skinfoldMean_mm: 13.4 - i * 0.05, sites: { suprailiac: 12.6 - i * 0.05, umbilical: 14.2 - i * 0.05 } } : {}),
    ...(i % 4 === 1 ? { waist_cm: 86 - i * 0.05 } : {}),
  }));
  readonly overviewModel: BodyCompOverviewModel = buildBodyCompOverviewModel({
    firstDayIso: this.bandDays[0],
    lastDayIso: this.bandDays[13],
    trendKind: OverviewTrendKind.Straight,
    describesNow: true,
    readings: this.overviewReadings,
    skinfoldSites: [{ id: 'suprailiac', name: untranslatedString('Suprailiac') }, { id: 'umbilical', name: untranslatedString('Umbilical') }],
    planCheck: {
      needsAdjustment: false,
      actionDecided: true,
      message: PlanUpdateUserMessage.AllOk,
      estimatedBW_kg: 82.4,
      weightSlope_kgwk: -0.42,
      fatnessSlopeBottom: -0.06,
      fatnessSlopeTop: -0.04,
      fatnessSeries: BodyFatSeries.Calipers,
      firstCountedDayIso: this.bandDays[0],
    },
    statusRule: ProgressStatusRule.Home,
    checkup: {
      dateIso: this.bandDays[13],
      calorieIntakeBaseline: 2416,
      selectedCalorieIntakeBaseline: CalorieIntakeBaseline.Logged,
      hasTickedDays: true,
      days: [2400, 2500, 2350, 900, 2430, 1200, 2400].map((consumed, i) => ({
        dateIso: this.bandDays[7 + i],
        label: untranslatedString('MTWTFSS'[i]),
        consumed,
        target: 2450,
        ticked: i !== 3 && i !== 5,
      })),
      training: { plannedDays: 5, doneDays: 4 },
    },
    showReportedIntake: true,
    weightUnit: { factor: 1, unitName: 'kg' },
    lengthUnit: { factor: 1, unitName: 'cm' },
    formatDate: dateIso => dateIso,
  });
}

/** History rows for the coach chart samples: each entry is [months before this one, awake, asleep],
 *  placed on the 10th of that month, so a few rows can share a month. */
function sampleCoachRows(route: CoachHistoryRoute, entries: [number, number, number][], tierCap?: number): CoachHistoryChartRow[] {
  return entries.map(([monthsBack, awake, asleep], index) => ({
    at_utc: DateTime.utc().startOf('month').minus({ months: monthsBack }).plus({ days: 1 + index, hours: 9 }).toISO()!,
    route,
    clientsAwake: awake,
    clientsAsleep: asleep,
    tierCap,
  }));
}
